import React, { useState, useEffect } from 'react';
import { MessageSquare, Mail, Lock, Activity, ChevronDown, ChevronRight, CalendarPlus, CalendarClock } from 'lucide-react';
import api from '../../services/api';
import ClientUpdatePanel from './ClientUpdatePanel';
import CaseEmails from './CaseEmails';
import CaseNotes from './CaseNotes';
import ProposeAppointmentModal from '../appointments/ProposeAppointmentModal';
import PendingRequestsPanel from '../appointments/PendingRequestsPanel';
import RescheduleAppointmentModal from '../appointments/RescheduleAppointmentModal';

// Tab-consolidation: Communication unifies the case's "what was said" surfaces.
//
// Reconciliation of the two virgo_case_updates writers — CANONICAL = the feed
// (client_portal_messages). A staff post through the feed also writes a
// virgo_case_updates mirror row (update_type='client_message') for the legacy
// case-timeline/Overview consumers; that mirror is SUPPRESSED here so a feed
// post renders exactly once (in Conversation). The Activity log below shows only
// non-message case updates (system/status events), so nothing double-renders.
// Going forward, client-facing updates are posted via the feed.
//
// Internal Notes read the separate CaseNote store, which the portal never reads
// (portal surfaces client_portal_messages + client-visible case updates only).
const SUBVIEWS = [
  { id: 'conversation', label: 'Conversation', icon: MessageSquare },
  { id: 'emails', label: 'Emails', icon: Mail },
  { id: 'notes', label: 'Internal notes', icon: Lock },
];

const CaseCommunicationTab = ({ caseId }) => {
  const [view, setView] = useState('conversation');
  const [activity, setActivity] = useState([]);
  const [activityError, setActivityError] = useState(null);
  const [showActivity, setShowActivity] = useState(false);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [pendingReload, setPendingReload] = useState(0);
  const [upcomingAppt, setUpcomingAppt] = useState(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [apptReload, setApptReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api.get(`/client-portal/staff/cases/${caseId}/upcoming-appointment`)
      .then((r) => { if (!cancelled) setUpcomingAppt(r.data?.appointment || null); })
      .catch(() => { if (!cancelled) setUpcomingAppt(null); });
    return () => { cancelled = true; };
  }, [caseId, apptReload]);

  useEffect(() => {
    let cancelled = false;
    setActivityError(null);
    api.get(`/cases/${caseId}/updates`)
      .then((r) => {
        if (cancelled) return;
        const rows = Array.isArray(r.data) ? r.data : (r.data.updates || []);
        // Exclude the feed's own mirror rows — those render in Conversation.
        // Case-folded: the table holds BOTH casings for one concept —
        // 'client_message' (staff replies via the feed) and 'CLIENT_MESSAGE'
        // (client-authored portal messages). Comparing the literal suppressed
        // only the staff half, so client messages rendered twice.
        setActivity(rows.filter(
          (u) => String(u.update_type || '').toLowerCase() !== 'client_message'));
      })
      .catch((err) => {
        // Never substitute an empty list for a failure. This catch used to
        // setActivity([]), and the section below only renders when non-empty —
        // so a 500 produced no error, no empty state, and no heading. There was
        // no way to tell "this failed" from "this matter has no activity".
        if (cancelled) return;
        setActivity([]);
        setActivityError(
          err?.response?.status
            ? `Could not load the case activity log (HTTP ${err.response.status}).`
            : 'Could not load the case activity log.');
      });
    return () => { cancelled = true; };
  }, [caseId]);

  return (
    <div className="p-6">
      <div className="flex gap-2 mb-4 items-center">
        {SUBVIEWS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setView(id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
              view === id
                ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                : 'bg-virgo-navy-600 text-virgo-teal-300 border border-virgo-teal-700/50 hover:bg-virgo-navy-500'
            }`}
          >
            <Icon size={14} /> {label}
          </button>
        ))}
        <button onClick={() => setProposeOpen(true)} className="ml-auto px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 bg-virgo-navy-600 text-virgo-teal-300 border border-virgo-teal-700/50 hover:bg-virgo-navy-500">
          <CalendarPlus size={14} /> Propose Appointment
        </button>
      </div>

      {upcomingAppt && (
        <div className="mb-4 flex items-center justify-between bg-virgo-navy-800/60 border border-virgo-teal-700/30 rounded-lg px-4 py-3">
          <div className="text-sm text-virgo-teal-100 flex items-center gap-2">
            <CalendarClock size={15} className="text-virgo-teal-400" />
            <span>
              Upcoming appointment: <strong>{upcomingAppt.local_date}</strong> at <strong>{upcomingAppt.local_time} {upcomingAppt.timezone_label}</strong>
              {' '}· {upcomingAppt.contact_name}
              {upcomingAppt.meeting_type ? ` · ${upcomingAppt.meeting_type.replace('_', '-')}` : ''}
              {upcomingAppt.status === 'pending_approval' ? ' · pending approval' : ''}
            </span>
          </div>
          <button onClick={() => setRescheduleOpen(true)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-virgo-navy-600 text-virgo-teal-300 border border-virgo-teal-700/50 hover:bg-virgo-navy-500">
            Reschedule
          </button>
        </div>
      )}

      <PendingRequestsPanel caseId={caseId} reloadKey={pendingReload} />
      <ProposeAppointmentModal isOpen={proposeOpen} onClose={() => setProposeOpen(false)} caseId={caseId} onCreated={() => setPendingReload((n) => n + 1)} />
      <RescheduleAppointmentModal isOpen={rescheduleOpen} onClose={() => setRescheduleOpen(false)} caseId={caseId} appointment={upcomingAppt}
        onRescheduled={() => setApptReload((n) => n + 1)} />

      {view === 'conversation' && (
        <div>
          {/* ClientUpdatePanel has its own padding; strip the extra by rendering inline */}
          <div className="-mx-6 -mt-2">
            <ClientUpdatePanel caseId={caseId} />
          </div>

          {activityError && (
            <div className="mt-4 border-t border-virgo-navy-600 pt-3">
              <div className="text-sm rounded-lg px-3 py-2 bg-amber-500/10 border border-amber-500/40 text-amber-200">
                {activityError} This is a load failure, not an empty file — the
                entries may still exist. Retry, or report it if it persists.
              </div>
            </div>
          )}

          {!activityError && activity.length > 0 && (
            <div className="mt-4 border-t border-virgo-navy-600 pt-3">
              <button
                onClick={() => setShowActivity((v) => !v)}
                className="flex items-center gap-2 text-sm text-virgo-teal-400 hover:text-virgo-teal-200"
              >
                {showActivity ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <Activity size={14} /> Case activity log ({activity.length})
              </button>
              {showActivity && (
                <div className="mt-2 space-y-2">
                  {activity.map((u) => {
                    // Imported correspondence from the 2025-12-08 migration. It has
                    // never been visible anywhere in Virgo, so on most matters it
                    // arrives all at once — label it as recovered history rather
                    // than letting it read as the tab breaking in a new direction.
                    const imported = ['emailcommunication', 'phonecommunication', 'communication']
                      .includes(String(u.update_type || '').toLowerCase());
                    return (
                    <div key={u.id} className="text-sm bg-virgo-navy-800/40 border border-virgo-teal-700/20 rounded-lg px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 min-w-0">
                          <span className="text-xs text-virgo-teal-500 uppercase">{u.update_type || 'note'}</span>
                          {/* Disclosure state, from visible_to_client — the field the
                              portal actually queries. Without this, an internal note
                              and a message the client can read look identical. */}
                          {u.visible_to_client
                            ? <span className="text-[10px] px-1.5 py-0.5 rounded border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 whitespace-nowrap">Client can see this</span>
                            : <span className="text-[10px] px-1.5 py-0.5 rounded border border-virgo-teal-700/40 bg-virgo-navy-700/40 text-virgo-teal-400 whitespace-nowrap">Internal</span>}
                          {imported && <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-500/40 bg-slate-500/10 text-slate-300 whitespace-nowrap">Imported</span>}
                        </span>
                        <span className="text-xs text-virgo-teal-500 whitespace-nowrap">
                          {u.created_at ? new Date(u.created_at).toLocaleString() : ''}
                        </span>
                      </div>
                      <p className="text-virgo-teal-100 whitespace-pre-wrap">{u.content}</p>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {view === 'emails' && <div className="-mx-6"><CaseEmails caseId={caseId} /></div>}
      {view === 'notes' && <div className="-mx-6"><CaseNotes caseId={caseId} /></div>}
    </div>
  );
};

export default CaseCommunicationTab;
